/**
 * Product Gallery Thumbnail Switcher
 *
 * Handles thumbnail click/keyboard interaction to update the main product image.
 * Accessibility: role="button", tabindex, aria-current, aria-live announcement.
 * Progressive enhancement: works without JS via SSR-rendered featured image.
 */

(function () {
  'use strict';

  function initGallery(gallery) {
    const mainImg = gallery.querySelector('#product-image__thumbnail');
    if (!mainImg) return;

    const items = Array.from(gallery.querySelectorAll('.product-image__item'));
    if (!items.length) return;

    // --- Live region for screen reader announcements ---
    const liveRegion = document.createElement('span');
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.className = 'product-gallery__live-region';
    gallery.appendChild(liveRegion);

    // --- Enhance each thumbnail item ---
    items.forEach((item, index) => {
      const img = item.querySelector('img');
      const altText = img ? img.alt : '';
      const label = altText ? `画像を表示: ${altText}` : `商品画像 ${index + 1}`;

      item.setAttribute('role', 'button');
      item.setAttribute('tabindex', '0');
      item.setAttribute('aria-label', label);

      // Mark first item as current (matches the SSR-rendered featured image)
      if (index === 0) {
        item.setAttribute('aria-current', 'true');
      }
    });

    // --- Shared handler ---
    function selectItem(item) {
      const img = item.querySelector('img');
      if (!img) return;

      // Swap main image attributes
      mainImg.src = img.currentSrc || img.src;

      const srcset = img.getAttribute('srcset');
      if (srcset) {
        mainImg.setAttribute('srcset', srcset);
      } else {
        mainImg.removeAttribute('srcset');
      }

      const sizes = img.getAttribute('sizes');
      if (sizes) {
        mainImg.setAttribute('sizes', sizes);
      } else {
        mainImg.removeAttribute('sizes');
      }

      // Keep alt meaningful; fall back to existing main alt
      if (img.alt) {
        mainImg.alt = img.alt;
      }

      // Update aria-current state on all items
      items.forEach((el) => el.removeAttribute('aria-current'));
      item.setAttribute('aria-current', 'true');

      // Announce to screen readers
      const announcement = img.alt ? `表示中: ${img.alt}` : '画像を切り替えました';
      liveRegion.textContent = '';
      // Defer to ensure the DOM change is noticed by screen readers
      setTimeout(() => {
        liveRegion.textContent = announcement;
      }, 50);
    }

    // --- Event delegation: click ---
    gallery.addEventListener('click', (event) => {
      const item = event.target.closest('.product-image__item');
      if (!item) return;
      selectItem(item);
    });

    // --- Event delegation: keyboard ---
    gallery.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const item = event.target.closest('.product-image__item');
      if (!item) return;
      if (event.key === ' ') event.preventDefault();
      selectItem(item);
    });
  }

  function init() {
    document.querySelectorAll('[data-product-gallery]').forEach(initGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
